import React from 'react'
import { cn } from '@/lib/utils'
import { AsyncContentProgress } from '@/shared/ui'
import { OpportunityCompactCard } from '@/modules/oportunidades/components/OpportunityCompactCard'
import type { OpportunityItem } from '@/modules/oportunidades/types'
import { useLinkedOpportunityAnalyses } from '../hooks/useLinkedOpportunityAnalyses'
import { AnalysisPostPreviewCard } from './AnalysisPostPreviewCard'

type AnalysisLinkedOpportunityHistoryProps = {
  opportunityId: number | null
  linkedOpportunity?: OpportunityItem | null
  thermoAllowed: boolean
  noteForMaster?: string
  className?: string
}

export function AnalysisLinkedOpportunityHistory({
  opportunityId,
  linkedOpportunity,
  thermoAllowed,
  noteForMaster,
  className,
}: AnalysisLinkedOpportunityHistoryProps) {
  const safeOpportunityId =
    opportunityId != null && Number.isFinite(Number(opportunityId))
      ? Math.floor(Number(opportunityId))
      : null

  const { items, loading, error } = useLinkedOpportunityAnalyses(
    safeOpportunityId,
    safeOpportunityId != null,
  )

  if (safeOpportunityId == null) return null

  const renderFooter = () =>
    linkedOpportunity ? (
      <OpportunityCompactCard
        item={linkedOpportunity}
        thermoAllowed={thermoAllowed}
        noteForMaster={noteForMaster}
        interactive={false}
      />
    ) : (
      <div className="rounded-xl bg-background/20 p-3">
        <AsyncContentProgress loading label="Carregando oportunidade...">
          {null}
        </AsyncContentProgress>
      </div>
    )

  if (loading) {
    return (
      <div className="rounded-xl bg-background/20 p-3">
        <AsyncContentProgress loading label="Carregando historico de analises...">
          {null}
        </AsyncContentProgress>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
        Nao foi possivel carregar o historico de analises.
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="text-xs text-foreground/60">
        Nenhuma analise publicada para esta oportunidade.
      </div>
    )
  }

  return (
    <div className={cn('relative pl-8', className)}>
      <div className="absolute left-3 top-0 h-full w-px bg-border/40" />
      <div className="space-y-6">
        {items.map((analysis) => (
          <div key={analysis.id} className="relative">
            <div className="absolute -left-[17px] top-6 h-3 w-3 rounded-full border border-border bg-[#1b1b1b]" />
            <AnalysisPostPreviewCard
              title={analysis.title}
              contentHtml={analysis.contentHtml}
              attachments={analysis.attachments}
              lightboxVariant="drawer"
              footer={renderFooter()}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
